import { graphql, Script } from 'gatsby';

import GlobalHead from '../layout/head';
import Layout from '../layout';
import LayoutTheme from './theme';
import PostLayout from './layout';

import { useBlogMentions } from '../../hooks/useBlogMentions';

import type { IEditorialData, IPostContext, IPostTemplateData } from './types';
import type { HeadProps, PageProps } from 'gatsby';

const PostTemplate = ({ data, pageContext, ...pageProps }: PageProps<IPostTemplateData, IPostContext>) => {
  const {
    typeNameDataKey,
  } = pageContext;
  const layoutData = data[typeNameDataKey];

  return (
    <Layout
      {...pageProps}
      {...pageContext}
      bodyClass={['editorial', typeNameDataKey].join(' ')}
      showBreadcrumbs={false}
      templateName="Editorial"
      theme={LayoutTheme}
      scrollIndicator={pageContext?.isPost}
    >
      <PostLayout
        data={{ ...(layoutData as IEditorialData), ...pageContext }}
      />
    </Layout>
  );
};

export const query = graphql`
  query EditorialTemplate(
    $id: String!,
    $wpCaseStudy: Boolean = false,
    $wpGlossaryPage: Boolean = false,
    $wpIntegratePage: Boolean = false,
    $wpLegalContent: Boolean = false,
    $wpNewsroomArticle: Boolean = false,
    $wpPage: Boolean = false,
    $wpPost: Boolean = false,
    $wpPrivacyPage: Boolean = false,
    $wpProductsPage: Boolean = false,
  ) {
    wpCaseStudy(id: {eq: $id}) @include(if: $wpCaseStudy) {
      ...CaseStudy
    }
    wpGlossaryPage(id: {eq: $id}) @include(if: $wpGlossaryPage) {
      ...GlossaryPage
    }
    wpIntegratePage(id: {eq: $id}) @include(if: $wpIntegratePage) {
      ...IntegratePage
    }
    wpLegalContent(id: {eq: $id}) @include(if: $wpLegalContent) {
      ...LegalContent
    }
    wpNewsroomArticle(id: {eq: $id}) @include(if: $wpNewsroomArticle) {
      ...NewsroomArticleDetails
    }
    wpPage(id: {eq: $id}) @include(if: $wpPage) {
      ...EditorialPage
    }
    wpPost(id: {eq: $id}) @include(if: $wpPost) {
      ...EditorialPost
    }
    wpPrivacyPage(id: {eq: $id}) @include(if: $wpPrivacyPage) {
      ...PrivacyPageContent
    }
    wpProductsPage(id: {eq: $id}) @include(if: $wpProductsPage) {
      ...ProductsPage
    }
  }
`;

export const Head = ({
  data,
  location,
  pageContext,
}: HeadProps<IPostTemplateData, IPostContext>) => {
  const { shortTitle = '', typeNameDataKey } = pageContext;
  const layoutData = data[typeNameDataKey];
  const includeFaqSchema = (layoutData?.content || '').includes('data-includefaqschema="true"');

  return (
    <GlobalHead
      authors={layoutData?.authors}
      excerpt={layoutData?.excerpt}
      isPost={pageContext.isPost}
      mentions={useBlogMentions(layoutData?.content || '')}
      pathname={location.pathname}
      seo={layoutData?.headlessSeo}
      shortTitle={shortTitle}
      translations={layoutData?.translations}
      typeNameDataKey={typeNameDataKey}
    >
      {data[typeNameDataKey]?.content.includes('https://platform.twitter.com/widgets.js') && <Script id="twitter-wjs" src="https://platform.twitter.com/widgets.js"/>}
      {data[typeNameDataKey]?.content.includes('https://player.vimeo.com/video') && <Script id="vimeo-player" src="//player.vimeo.com/api/player.js"/>}
      {data[typeNameDataKey]?.content.includes('https://www.tiktok.com/embed.js') && <Script id="tiktok-player" src="https://www.tiktok.com/embed.js"/>}
      {includeFaqSchema ? (<html itemScope itemType="https://schema.org/FAQPage" />) : null}
    </GlobalHead>
  );
};

export default PostTemplate;
